import { StacksBySite } from '@pubstack/common/src/stack'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent } from 'react'
import { WithClassName } from '~/types/utils'

type StackUsageProps = WithClassName & {
  sitesStacks?: StacksBySite
}

/**
 * How widely a thing is used, as a count rather than a list.
 *
 * This used to render every media as a folding row of stack names and versions, which answered a
 * question nobody was asking: a publisher opening a flyout wants to know whether changing this is safe,
 * and "12 stacks" settles that on sight where twelve collapsed rows do not. The rule flyouts already
 * say it this way, and these read next to each other.
 */
const BaseStackUsage: FunctionComponent<StackUsageProps> = ({ className, sitesStacks }) => {
  const mediaCount = Object.keys(sitesStacks ?? {}).length
  const stackCount = Object.values(sitesStacks ?? {}).reduce((total, stacks) => total + stacks.length, 0)

  return (
    /* Rendered even when empty — "used by nothing" is the one answer a publisher about to change
       something most wants, and hiding the section withholds it. */
    <section className={cn('flex flex-col gap-2 py-2', className)}>
      <h2 className={'m-0 font-medium'}>
        Used by {mediaCount} media{mediaCount === 1 ? '' : 's'}
        {stackCount > 0 && <span className={'font-normal text-slate-500'}>{` · ${stackCount} stack${stackCount === 1 ? '' : 's'}`}</span>}
      </h2>
      {stackCount === 0 && <span className={'text-slate-grey'}>Not used by any stack.</span>}
    </section>
  )
}

export const StackUsage = BaseStackUsage
